import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase/client';
import { useAuth } from './useAuth';

export interface Notification {
  id: string;
  type: string;
  title: string;
  body: string;
  read: boolean;
  created_at: string;
}

/**
 * Maps the DB notification_log row to the local Notification interface.
 * DB schema: id, notification_type, title, body, status, sent_at, error_message, user_id
 * We treat status === 'read' as read, and use sent_at as the created_at timestamp.
 */
function mapRow(row: {
  id: string;
  notification_type: string;
  title: string;
  body: string;
  status: string | null;
  sent_at: string | null;
}): Notification {
  return {
    id: row.id,
    type: row.notification_type,
    title: row.title,
    body: row.body,
    read: row.status === 'read',
    created_at: row.sent_at || new Date().toISOString(),
  };
}

export function useNotifications() {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) return;

    // Load recent notifications
    const loadNotifications = async () => {
      const { data } = await supabase
        .from('notification_log')
        .select('*')
        .eq('user_id', user.id)
        .order('sent_at', { ascending: false })
        .limit(20);

      if (data) {
        const mapped = data.map(mapRow);
        setNotifications(mapped);
        setUnreadCount(mapped.filter(n => !n.read).length);
      }
    };

    loadNotifications();
  }, [user]);

  const markAsRead = useCallback(async (id: string) => {
    if (!user) return;
    await supabase
      .from('notification_log')
      .update({ status: 'read' })
      .eq('id', id)
      .eq('user_id', user.id);

    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, [user]);

  const markAllRead = useCallback(async () => {
    if (!user) return;
    await supabase
      .from('notification_log')
      .update({ status: 'read' })
      .eq('user_id', user.id)
      .neq('status', 'read');

    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, [user]);

  return { notifications, unreadCount, markAsRead, markAllRead };
}
